import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Grid,
  InputAdornment,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './Login.css';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();

  // Page content'teki JavaScript fonksiyonlarını ekle
  useEffect(() => {
    // reCAPTCHA site key
    (window as any).recaptcha_sitekey = "6LeiihUjAAAAAKnRigR2vKGRVkglpvYz5DayNtwE";
    
    // Language setting
    (window as any).lang = "tr";
    
    // OTP email check setting
    (window as any).otp_email_check_and_spv_access = false;

    // Hash validation
    const validHashes = ["#/signup", "#/renew-password", "#/reset-password", "#/new-password", "#/logindenied", "#/ipchanged"];
    const currentHash = window.location.hash;
    if (currentHash && !validHashes.some(s => currentHash.includes(s))) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Intercom settings
    (window as any).intercomSettings = {
      app_id: "k1fs0jgl",
      name: "",
      email: "",
      created_at: Math.floor(new Date().getTime() / 1000),
      user_tenant_url: "https://personelika.alo-tech.com",
      user_tenant_name: "personelika.alo-tech.com",
      user_role: "",
      user_is_new_agent_screen: true,
      user_language: "tr",
      company: {
        id: "ahRzfm11c3RlcmktaGl6bWV0bGVyaXIkCxIGVGVuYW50IhhwZXJzb25lbGlrYS5hbG8tdGVjaC5jb20MogEYcGVyc29uZWxpa2EuYWxvLXRlY2guY29t",
        name: "personelika.alo-tech.com",
        created_at: Math.floor(new Date("16 Ağustos 2022 15:26").getTime() / 1000),
        upgraded_at: Math.floor(new Date("22 Eylül 2025 09:50").getTime() / 1000)
      }
    };

    // Intercom initialization
    const initIntercom = () => {
      const e = window as any;
      const t = e.Intercom;
      if (typeof t === "function") {
        t("reattach_activator");
        t("update", e.intercomSettings);
      } else {
        const n = document;
        const a = function(args: any) { a.c(args); };
        (a as any).q = [];
        a.c = function(e: any) { (a as any).q.push(e); };
        e.Intercom = a;
        const r = function() {
          const e = n.createElement("script");
          e.type = "text/javascript";
          e.async = true;
          e.src = "https://widget.intercom.io/widget/k1fs0jgl";
          const t = n.getElementsByTagName("script")[0];
          t.parentNode?.insertBefore(e, t);
        };
        if (e.attachEvent) {
          e.attachEvent("onload", r);
        } else {
          e.addEventListener("load", r, false);
        }
      }
    };

    // Initialize when document is ready
    if (document.readyState === "complete") {
      initIntercom();
    } else {
      document.addEventListener("readystatechange", function() {
        if (document.readyState === "complete") {
          initIntercom();
        }
      });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔐 Login attempt:', formData.email);
      const response = await authService.login(formData.email, formData.password);
      console.log('🔐 Login response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Login successful, navigating to dashboard');
        // ✨ App.tsx'teki isAuthenticated state'ini güncellemek için sayfayı yenile
        // navigate('/') yerine window.location kullanarak full page reload yap
        window.location.href = '/';
      } else {
        console.error('❌ Login failed:', response.message);
        setError(response.message || 'Giriş başarısız');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError('Giriş sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        fontFamily: 'Axiforma, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        overflow: 'hidden',
        backgroundImage: 'url(https://images.unsplash.com/photo-1634712282287-14ed57b9cc89?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YXJtY2hhaXJ8ZW58MHx8MHx8fDA%3D)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        },
      }}
    >
      <Grid container sx={{ height: '100%', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
        {/* Sol Panel - Illustration */}
        <Grid component="div" size={{ xs: 12, md: 7 }} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px',
              position: 'relative',
            }}
          >
            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '15%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '10%',
                left: '10%',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.15)',
                filter: 'blur(50px)',
              }}
            />
            
            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/percrm-logo.svg`}
                alt="PerCRM Logo"
                onError={(e: any) => { 
                  console.error('Logo yüklenemedi:', e.target.src);
                  e.target.onerror = null;
                  e.target.src = `${process.env.PUBLIC_URL}/logo.svg`;
                }}
                sx={{
                  width: '100%',
                  maxWidth: '220px',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto 24px',
                  filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3))',
                }}
              />
              
              <Typography
                sx={{
                  fontSize: '18px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.8,
                  maxWidth: '500px',
                  margin: '0 auto',
                  textShadow: '0 1px 10px rgba(0, 0, 0, 0.1)',
                }}
              >
                Yeni nesil çözüm merkezi platformu ile müşteri deneyiminizi
                en üst seviyeye taşıyın.
              </Typography>

              {/* Feature badges */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  marginTop: '48px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {['Akıllı Yönlendirme', 'Gerçek Zamanlı Raporlar', 'Kolay Entegrasyon'].map((feature) => (
                  <Box
                    key={feature}
                    sx={{
                      padding: '12px 24px',
                      borderRadius: '24px',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {feature}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Sağ Panel - Login Form */}
        <Grid component="div" size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '480px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: '30px',
                padding: '48px',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37), inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                borderTop: '1px solid rgba(255, 255, 255, 0.4)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '30px',
                  padding: '2px',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                },
              }}
            >
              {/* Title */}
              <Typography
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '32px',
                  textAlign: 'center',
                  marginBottom: '12px',
                  marginTop: '8px',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                }}
              >
                Hoş Geldiniz
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '15px',
                  textAlign: 'center',
                  marginBottom: '32px',
                  lineHeight: 1.6,
                  textShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
                }}
              >
                Devam etmek için giriş yapın
              </Typography>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: '12px',
                    backgroundColor: '#FEE2E2',
                    color: '#DC2626',
                    border: '1px solid #FCA5A5',
                    '& .MuiAlert-icon': {
                      color: '#DC2626'
                    },
                    '& .MuiAlert-message': {
                      fontSize: '0.9rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {/* Email Field */}
                <Box sx={{ marginBottom: '20px' }}>
                  <Typography 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 1,
                      ml: 0.5
                    }}
                  >
                    E-posta
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="ornek@email.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        color: '#ffffff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: '2px',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 18px',
                        fontSize: '15px',
                        color: '#ffffff',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box sx={{ marginBottom: '16px' }}>
                  <Typography 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 500, 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 1,
                      ml: 0.5
                    }}
                  >
                    Parola
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'rgba(255, 255, 255, 0.9)',
                              padding: '0 8px',
                              transition: 'color 0.3s ease',
                              '&:hover': {
                                color: '#ffffff',
                              },
                            }}
                          >
                            {showPassword ? 'Gizle' : 'Göster'}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        color: '#ffffff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          borderWidth: '2px',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                            borderWidth: '2px',
                          },
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '16px 18px',
                        fontSize: '15px',
                        color: '#ffffff',
                        '&::placeholder': {
                          color: 'rgba(255, 255, 255, 0.5)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>

                {/* Forgot Password Link */}
                <Box sx={{ marginBottom: '28px', display: 'flex', justifyContent: 'flex-end' }}>
                  <Link
                    href="#"
                    sx={{
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#ffffff',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Şifremi Unuttum
                  </Link>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  disabled={loading || !formData.email || !formData.password}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#fff',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&:disabled': {
                      background: '#E5E7EB',
                      color: '#9CA3AF',
                      boxShadow: 'none',
                    },
                    marginBottom: '24px',
                    marginTop: '8px',
                  }}
                >
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </Button>
              </form>

              {/* Signup Link */}
              <Box
                sx={{
                  textAlign: 'center',
                  width: '100%',
                  marginTop: '24px',
                  fontSize: '14px',
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    marginRight: '4px',
                  }}
                >
                  Hesabınız yok mu?
                </Typography>
                <Link
                  href="/signup"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Kayıt Olun
                </Link>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;